import { Moment } from 'moment'

export type GOScheduleMenu = {
  id: string
  allMenuItems: SchedulePlannerMenuItem[]
  avgPrice: number
  avgMenuMargin: number
  avgMenuItemRatingByClient: string
  chef: GOSchedMenuChef
  chefName: string
  chefPriorityType: string
  cuisineTags: string[]
  cuisinesId: string // normalized id for all the cuisine tags on a menu for easy comparison
  cutoffTime: string
  clientName: string
  distance: string | number
  geoCoordinates: Record<number, number[]> // longitude, latitude
  groupOrderCutoffDays: number
  groupOrderCutoffTime: number
  lastOrdered: string
  margin: number
  marketType: string
  menuMaxItems: number
  name: string
  pickupTime: string
  groupOrderEarliestPickupTime: number | null
  readyToHeat: boolean
  vendorMaxGOItems: number
}

export interface Coordinate {
  latitude: number
  longitude: number
}

export type GOSchedMenuChef = {
  id: string
  name: string
  coordinates: Coordinate
}

export interface ChefMenuGroup {
  id: string
  name: string
  chef: GOSchedMenuChef
  chefPriorityType: string
  coordinates: Coordinate
  menus: GOScheduleMenu[]
}

export interface GOClientSettings {
  id: string
  name: string
  location: GOClientLocation
}

export interface GOClientGlobalSettings {
  id: string
  budgetPerPersonSettings: Record<string, { value: number; type: string }>
  isCustomBudgetPerPerson: boolean
}

export interface GOClientLocation {
  id: string
  name: string
  line1: string
  line2: string
  city: string
  state: string
  zip: string
  coordinates: Coordinate
}

export type SchedulePlannerMenuItem = {
  name: string
}

export type FilterConfig = {
  default: string
  information?: string
  disabledKey?: string
  name: string
  key: string
  label: string
  needsInfo?: boolean
  presenter?: () => void
  loaderFunction?: () => void
  placeHolder: string
  options: any[]
}

export type Cuisine = {
  id: string
  name: string
}

export type Account = {
  id: string
  name: string
  pin: number
  addresses: Address[]
  contacts: Contact[]
}

export type Address = {
  id: string
  city: string
  line1: string
  line2: string
  latitude: string
  longitude: string
  state: string
  zip: string
}

export const isAddress = (value: any): value is Address =>
  value &&
  'line1' in value &&
  'city' in value &&
  'state' in value &&
  'zip' in value

export type Contact = {
  id: string
  firstName: string
  lastName: string
  name: string
  email: string
  phoneNumber: string
}

export const isContact = (value: any): value is Contact =>
  value &&
  'email' in value &&
  'formattedDescription' in value && // Unique to Contact
  'contactableId' in value

export type Option = {
  value: string
  label: string
}

export type FilterOption = string | Moment | boolean | number | string[] | null

export enum AverageMenuPrice {
  LESS_THAN_TEN = 'lt_10',
  GREATER_THAN_10 = 'gt_10',
  TEN_TO_FIFTEEN = 'TEN_TO_FIFTEEN',
}

export enum AverageMenuMargin {
  LESS_THAN_75 = 'lt_75',
  GREATER_THAN_50 = 'gt_50',
}

export enum VendorMaxGroupOrderItems {
  GREATER_THAN_100 = 'gt_100',
  LESS_THAN_100 = 'lt_100',
}

export enum VendorMenucardCap {
  GREATER_THAN_100 = 'gt_100',
  LESS_THAN_100 = 'lt_100',
}

export enum Distance {
  LESS_THAN_10_MILES = 'lt_10',
  LESS_THAN_20_MILES = 'lt_20',
}

export enum LastScheduled {
  LESS_THAN_1_WEEK = 'lt_1',
  LESS_THAN_2_WEEKS = 'lt_2',
  LESS_THAN_4_WEEKS = 'lt_4',
}

export interface MenuTemplate {
  chefIds?: string[]
  excludeChefIds?: string[]
  cuisines?: string[]
  excludeCuisines?: string[]
  maxNumReuses: number
}

export interface DateMenuTemplate {
  id: string
  createdAt: Moment
  updatedAt: Moment
  createdByUserId: string
  createdByEmail: string
  updatedByUserId: string
  updatedByEmail: string
  hqId?: number
  isPrivate: boolean
  name: string
  templates: MenuTemplate[]
}

export type ClusterTarget = GOClientLocation | GOSchedMenuChef

// Define DateMenuSuggestion interface
export interface DateMenuSuggestion {
  id: number
  target: ClusterTarget
  menus: GOScheduleMenu[]
  totalDistance: number
  route: number[]
}

export interface DateMenuSuggestionImport {
  id: number
  menuIds: string[]
  route: number[]
  target: ClusterTarget
}

export interface ShowInfoModalReq {
  title: string
  content: React.ReactNode
  width?: string
  height?: string
  noHeader?: boolean
  close?: () => void
}
